import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Login, ForgotPassword, ResetPassword, AcceptInvite } from "@/pages/Auth";
import { Register, VerifyEmail } from "@/pages/Register";
import GetStarted from "@/pages/GetStarted";
import AwaitingActivation from "@/pages/AwaitingActivation";
import PublicVerify from "@/pages/PublicVerify";
import { Privacy, Terms } from "@/pages/Legal";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Plants from "@/pages/Plants";
import Products from "@/pages/Products";
import Epds from "@/pages/Epds";
import EpdWizard from "@/pages/EpdWizard";
import Evidence from "@/pages/Evidence";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import Admin from "@/pages/Admin";

function Protected({ children, roles }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "org_admin" && !user.org_active) return <Navigate to="/awaiting-activation" replace />;
  if (user.needs_onboarding && pathname !== "/onboarding" && pathname !== "/app/billing") return <Navigate to="/onboarding" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function ActivationGate() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!(user.role === "org_admin" && !user.org_active)) return <Navigate to="/app" replace />;
  return <AwaitingActivation />;
}

const Shell = ({ children }) => <Layout>{children}</Layout>;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/verify/:publicId" element={<PublicVerify />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            <Route path="/awaiting-activation" element={<ActivationGate />} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />

            <Route path="/app" element={<Protected><Shell><Dashboard /></Shell></Protected>} />
            <Route path="/app/plants" element={<Protected><Shell><Plants /></Shell></Protected>} />
            <Route path="/app/products" element={<Protected><Shell><Products /></Shell></Protected>} />
            <Route path="/app/epds" element={<Protected><Shell><Epds /></Shell></Protected>} />
            <Route path="/app/epds/:epdId" element={<Protected><Shell><EpdWizard /></Shell></Protected>} />
            <Route path="/app/evidence" element={<Protected><Shell><Evidence /></Shell></Protected>} />
            <Route path="/app/settings" element={<Protected><Shell><Settings /></Shell></Protected>} />
            <Route path="/app/billing" element={<Protected><Shell><Billing /></Shell></Protected>} />
            <Route path="/app/admin" element={<Protected roles={["super_admin"]}><Shell><Admin /></Shell></Protected>} />

            <Route path="/" element={<Navigate to="/get-started" replace />} />
            <Route path="*" element={<Navigate to="/get-started" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
